import { ConnectStatus } from './types';

export type ExchangeStatusForResolve = 'idle' | 'loading' | 'success' | 'error' | 'expired';

interface ResolveStatusInput {
  authCode: string | null;
  isAuthCodeValid: boolean;
  /** useCountdown 등에 의해 클라이언트 측에서 만료 판단된 경우 true */
  didLocalExpire: boolean;
  exchangeStatus: ExchangeStatusForResolve;
  /**
   * 인증 확인이 끝났는지/결과는 무엇인지.
   *  - null:  아직 확인 중
   *  - true:  로그인 상태
   *  - false: 미로그인 상태
   */
  isAuthenticated: boolean | null;
  /**
   * 채널 리스트 fetch 완료 여부 + 본인 채널 개수.
   *  - hasFetchedApps === false: 아직 fetch 중
   *  - hasFetchedApps === true: 완료. ownedChannelCount 가 0이면 NoChannel, 1+이면 Ready.
   */
  hasFetchedApps: boolean;
  ownedChannelCount: number;
}

/**
 * 6 상태 결정 알고리즘 (순수 함수).
 *
 * 우선순위:
 *   1. authCode 없음/invalid       → error
 *   2. didLocalExpire 또는 exchange='expired' → expired
 *   3. exchange='error'            → error
 *   4. exchange='loading'|'idle'  → loading
 *   5. exchange='success' (이하 모두)
 *      a) isAuthenticated === null → loading (아직 확인 중)
 *      b) !isAuthenticated         → success-unauthenticated
 *      c) !hasFetchedApps          → loading
 *      d) ownedChannelCount === 0  → success-no-channel
 *      e) otherwise                 → success-ready
 */
export function resolveConnectStatus(input: ResolveStatusInput): ConnectStatus {
  const {
    authCode,
    isAuthCodeValid,
    didLocalExpire,
    exchangeStatus,
    isAuthenticated,
    hasFetchedApps,
    ownedChannelCount
  } = input;

  if (!authCode || !isAuthCodeValid) return 'error';
  if (didLocalExpire) return 'expired';
  if (exchangeStatus === 'expired') return 'expired';
  if (exchangeStatus === 'error') return 'error';
  if (exchangeStatus === 'loading' || exchangeStatus === 'idle') return 'loading';

  // exchangeStatus === 'success'
  if (isAuthenticated === null) return 'loading';
  if (!isAuthenticated) return 'success-unauthenticated';
  if (!hasFetchedApps) return 'loading';
  if (ownedChannelCount === 0) return 'success-no-channel';
  return 'success-ready';
}
